import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';
import { StatusType } from '@/shared/types/status.types';

/**
 * Status Badge Variants
 * Uses CVA for type-safe variant management with theme-aware CSS variables
 * Works consistently across light, dark, and madivladi themes
 */
const statusBadgeVariants = cva(
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors',
  {
    variants: {
      status: {
        [StatusType.Active]: 'bg-success-muted text-success',
        [StatusType.Inactive]: 'bg-destructive/10 text-destructive',
        [StatusType.Pending]: 'bg-warning-muted text-warning',
        [StatusType.Idle]: 'bg-muted text-muted-foreground',
      },
    },
    defaultVariants: {
      status: StatusType.Idle,
    },
  }
);

export interface StatusBadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  /**
   * The status to display
   */
  readonly status: StatusType;
}

/**
 * StatusBadge Component
 *
 * A reusable status indicator badge with theme support (light/dark/madivladi/system).
 * Uses the Zinc design system and provides semantic color coding for different states.
 *
 * @example
 * ```tsx
 * <StatusBadge status="active" />
 * <StatusBadge status="pending" />
 * <StatusBadge status="inactive" />
 * <StatusBadge status="idle" />
 * ```
 */
export function StatusBadge({ status, className, ...props }: StatusBadgeProps) {
  return (
    <span
      className={cn(statusBadgeVariants({ status }), className)}
      role="status"
      aria-label={`Status: ${status}`}
      {...props}
    >
      {status}
    </span>
  );
}

StatusBadge.displayName = 'StatusBadge';
